import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";

// ─── Types ────────────────────────────────────────────────────────────────────

type Role = "admin" | "editor" | "viewer";

const ROLE_INFO: Record<Role, { label: string; color: string; bg: string }> = {
  admin:  { label: "Admin",    color: "#3730A3", bg: "#ECEBFB" },
  editor: { label: "Editor",   color: "#46443E", bg: "#EAE7DF" },
  viewer: { label: "Viewer",   color: "#78756C", bg: "#F0EEE8" },
};

function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const initials = name.trim().split(" ").filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("");
  const colors = ["#1A1917","#46443E","#78756C","#4F46E5","#3F3F46","#52525B"];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, background: color, fontSize: size * 0.38, fontFamily: "Poppins, sans-serif" }}>
      {initials || "?"}
    </div>
  );
}

// ─── Modal de convite (HU-090) ────────────────────────────────────────────────

function ConviteModal({
  clientId,
  onSent,
  onClose,
}: {
  clientId: number;
  onSent: () => void;
  onClose: () => void;
}) {
  const [nome, setNome]   = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole]   = useState<Role>("viewer");
  const [resultado, setResultado] = useState<{
    inviteUrl: string;
    dev: boolean;
    emailStatus: "sent" | "no_api_key" | "failed" | null;
    emailError: string | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const inviteMutation = trpc.clientAuth.invite.useMutation({
    onSuccess: (res: any) => {
      setResultado({
        inviteUrl: res.inviteUrl,
        dev: res.dev,
        emailStatus: res.emailStatus ?? null,
        emailError: res.emailError ?? null,
      });
      // Já copia pro clipboard pra garantir
      navigator.clipboard.writeText(res.inviteUrl).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onSent();
    },
  });

  function copyLink() {
    if (!resultado) return;
    navigator.clipboard.writeText(resultado.inviteUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleSend() {
    if (!nome.trim() || !email.trim() || inviteMutation.isPending) return;
    inviteMutation.mutate({
      name: nome.trim(),
      email: email.trim(),
      clientId,
      role,
      origin: window.location.origin,
    });
  }

  const INPUT = "w-full border border-[var(--solar-line)] rounded-[10px] px-3 py-2.5 text-sm text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-soft)] focus:border-[var(--brand)] transition bg-white";

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-x-4 top-24 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md z-50 bg-white rounded-xl shadow-[var(--elev-3)] flex flex-col overflow-hidden" style={{ border: "1px solid var(--solar-line)" }}>
        <div className="flex items-center justify-between px-6 py-4 shrink-0" style={{ borderBottom: "1px solid var(--solar-line)" }}>
          <h2 className="font-bold text-base text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>Convidar usuário</h2>
          <button onClick={onClose} className="text-[var(--solar-mute)] hover:text-[var(--solar-ink)] transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {resultado ? (
          <div className="px-6 py-6 space-y-4">
            {/* Banner por status do email */}
            {resultado.emailStatus === "sent" && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-start gap-2">
                <svg className="w-5 h-5 text-green-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-green-800">Email enviado pra {email}</p>
                  <p className="text-xs text-green-700/80">Cheque também o spam se não chegar.</p>
                </div>
              </div>
            )}
            {resultado.emailStatus === "no_api_key" && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <p className="text-sm font-semibold text-amber-800">⚠ Email desativado</p>
                <p className="text-xs text-amber-700">RESEND_API_KEY não configurado. Envie o link manualmente.</p>
              </div>
            )}
            {resultado.emailStatus === "failed" && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-1">
                <p className="text-sm font-semibold text-red-800">⚠ Email FALHOU</p>
                <p className="text-xs text-red-700">
                  Resend rejeitou o envio (provável domínio não verificado). Copie o link abaixo e envie manualmente.
                </p>
                {resultado.emailError && (
                  <p className="text-[10px] text-red-600/80 font-mono break-all">{resultado.emailError.slice(0, 200)}</p>
                )}
              </div>
            )}

            {/* Link sempre visível + botão de copiar + atalhos */}
            <div className="rounded-[10px] bg-[var(--solar-bg)] border border-[var(--solar-line)] p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-[var(--solar-mute)] uppercase tracking-widest">Link do convite</p>
                <button
                  onClick={copyLink}
                  className="text-xs font-semibold px-2 py-1 rounded-[8px] border border-[var(--solar-ink)] text-[var(--solar-ink)] hover:bg-[var(--solar-ink)] hover:text-white transition-colors"
                >
                  {copied ? "✓ Copiado" : "Copiar"}
                </button>
              </div>
              <p className="text-xs text-[var(--ink-2)] break-all bg-white border border-[var(--solar-line)] rounded-[8px] p-2 font-mono">
                {resultado.inviteUrl}
              </p>
              <div className="flex gap-3 pt-1">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Olá ${nome}! Você foi convidado pro SocialHub. Acesse: ${resultado.inviteUrl}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-[11px] text-[var(--solar-mute)] hover:text-[var(--solar-ink)] underline"
                >
                  WhatsApp →
                </a>
                <a
                  href={`mailto:${email}?subject=${encodeURIComponent("Convite SocialHub")}&body=${encodeURIComponent(`Olá ${nome},\n\nVocê foi convidado pro SocialHub.\nAcesse: ${resultado.inviteUrl}\n\nO link expira em 72h.`)}`}
                  className="text-[11px] text-[var(--solar-mute)] hover:text-[var(--solar-ink)] underline"
                >
                  Email →
                </a>
              </div>
            </div>

            <button onClick={onClose}
              className="w-full py-2.5 rounded-[10px] bg-[var(--solar-ink)] text-white text-sm font-semibold shadow-[var(--elev-1)] hover:opacity-90 transition-opacity"
              style={{ fontFamily: "Poppins, sans-serif" }}>
              Concluir
            </button>
          </div>
        ) : (
          <>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide mb-1.5">Nome *</label>
                <input autoFocus type="text" value={nome} onChange={e => setNome(e.target.value)}
                  placeholder="Nome completo" className={INPUT} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide mb-1.5">E-mail *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
                  placeholder="email@empresa.com" className={INPUT} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide mb-1.5">Perfil de acesso</label>
                <div className="flex gap-2">
                  {(["admin", "editor", "viewer"] as Role[]).map(r => {
                    const info = ROLE_INFO[r];
                    const isActive = role === r;
                    return (
                      <button key={r} type="button" onClick={() => setRole(r)}
                        className="flex-1 px-3 py-2 rounded-[10px] text-xs font-semibold border-2 transition-all"
                        style={{
                          background: isActive ? info.bg : "white",
                          borderColor: isActive ? info.color : "var(--solar-line)",
                          color: isActive ? info.color : "var(--solar-mute)",
                        }}>
                        {info.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {inviteMutation.error && (
              <p className="px-6 pb-2 text-red-500 text-sm">{inviteMutation.error.message}</p>
            )}

            <div className="flex gap-3 px-6 py-4 shrink-0" style={{ borderTop: "1px solid var(--solar-line)" }}>
              <button onClick={onClose} className="flex-1 py-2.5 rounded-[10px] border border-[var(--solar-line)] text-[var(--solar-ink)] text-sm font-semibold hover:bg-[var(--solar-bg)] transition-colors">
                Cancelar
              </button>
              <button onClick={handleSend} disabled={!nome.trim() || !email.trim() || inviteMutation.isPending}
                className="flex-1 py-2.5 rounded-[10px] bg-[var(--solar-ink)] text-white text-sm font-semibold shadow-[var(--elev-1)] transition-all hover:opacity-90 disabled:opacity-40"
                style={{ fontFamily: "Poppins, sans-serif" }}>
                {inviteMutation.isPending ? "Enviando…" : "Enviar convite"}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Página Usuários ──────────────────────────────────────────────────────────

export default function Usuarios() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;
  const isRazAdmin = session?.isRazAdmin ?? false;
  const isAdmin = isRazAdmin || session?.role === "admin";

  const [busca, setBusca] = useState("");
  const [conviteOpen, setConviteOpen] = useState(false);

  const { data: usuarios = [], isLoading, refetch } = trpc.usuarios.list.useQuery(
    { clientId }, { enabled: !!clientId }
  );

  const updateRoleMutation   = trpc.usuarios.updateRole.useMutation({ onSuccess: () => refetch() });
  const toggleActiveMutation = trpc.usuarios.toggleActive.useMutation({ onSuccess: () => refetch() });
  const deleteMutation       = trpc.usuarios.delete.useMutation({ onSuccess: () => refetch() });

  const filtered = usuarios.filter(u =>
    !busca ||
    u.name.toLowerCase().includes(busca.toLowerCase()) ||
    u.email.toLowerCase().includes(busca.toLowerCase())
  );

  const ativos    = usuarios.filter(u => u.active).length;
  const admins    = usuarios.filter(u => u.role === "admin").length;

  return (
    <div className="h-full flex flex-col bg-[var(--solar-bg)]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-4 shrink-0">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <h1 className="font-bold text-lg text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>Usuários</h1>
            <p className="text-[var(--solar-mute)] text-xs mt-0.5">
              {usuarios.length} {usuarios.length === 1 ? "usuário" : "usuários"} · {ativos} ativo{ativos !== 1 ? "s" : ""} · {admins} admin{admins !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="relative min-w-48 max-w-72">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--solar-mute)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar usuários…"
              className="w-full pl-9 pr-3 py-2 border border-[var(--solar-line)] rounded-[10px] text-sm text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-soft)] focus:border-[var(--brand)] transition bg-white" />
          </div>
          {isAdmin && (
            <button
              onClick={() => setConviteOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--solar-ink)] text-white text-sm font-semibold rounded-[10px] shadow-[var(--elev-1)] transition-all hover:opacity-90 shrink-0"
              style={{ fontFamily: "Poppins, sans-serif" }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Convidar usuário
            </button>
          )}
        </div>
      </div>

      {conviteOpen && (
        <ConviteModal
          clientId={clientId}
          onSent={() => refetch()}
          onClose={() => setConviteOpen(false)}
        />
      )}

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--solar-ink)] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-4">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}>
              <svg className="w-8 h-8 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-semibold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
                {busca ? "Nenhum usuário encontrado" : "Sem usuários cadastrados"}
              </p>
              <p className="text-[var(--solar-mute)] text-sm mt-1">
                {busca ? "Tente outro termo" : "Os usuários convidados aparecerão aqui"}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] overflow-hidden">
            {/* Header da tabela */}
            <div className="grid grid-cols-[1fr_200px_120px_80px_60px] gap-4 px-5 py-3 border-b border-[var(--solar-line)] bg-[var(--solar-bg)]">
              <span className="text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>Usuário</span>
              <span className="text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>Email</span>
              <span className="text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>Perfil</span>
              <span className="text-[11px] font-semibold text-[var(--solar-mute)] uppercase tracking-wide" style={{ fontFamily: "Poppins, sans-serif" }}>Status</span>
              <span />
            </div>

            {/* Linhas */}
            <div className="divide-y divide-[var(--solar-line)]">
              {filtered.map(u => {
                const roleInfo = ROLE_INFO[u.role as Role] ?? ROLE_INFO.viewer;
                return (
                  <div key={u.id} className="grid grid-cols-[1fr_200px_120px_80px_60px] gap-4 items-center px-5 py-3.5 hover:bg-[var(--solar-bg)] transition-colors">
                    {/* Nome */}
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar name={u.name} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-semibold text-[var(--solar-ink)] truncate" style={{ fontFamily: "Poppins, sans-serif" }}>{u.name}</p>
                          {(u as any).isRazAdmin && (
                            <span
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0"
                              style={{ background: "var(--solar-ink)", color: "#fff", fontFamily: "Poppins, sans-serif" }}
                              title="Admin global RAZ — acessa todos os tenants"
                            >
                              RAZ
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-[var(--solar-mute)]">
                          Desde {new Date(u.createdAt).toLocaleDateString("pt-BR", { month: "short", year: "numeric" })}
                        </p>
                      </div>
                    </div>

                    {/* Email */}
                    <p className="text-xs text-[var(--solar-mute)] truncate">{u.email}</p>

                    {/* Role */}
                    {isRazAdmin ? (
                      <select
                        value={u.role}
                        onChange={e => updateRoleMutation.mutate({ id: u.id, role: e.target.value as Role })}
                        className="text-xs font-semibold rounded-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-[var(--brand-soft)] transition cursor-pointer"
                        style={{ background: roleInfo.bg, color: roleInfo.color }}
                        disabled={updateRoleMutation.isPending}
                      >
                        <option value="admin">Admin</option>
                        <option value="editor">Editor</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full w-fit" style={{ background: roleInfo.bg, color: roleInfo.color }}>
                        {roleInfo.label}
                      </span>
                    )}

                    {/* Status ativo/inativo */}
                    <button
                      onClick={() => isRazAdmin && toggleActiveMutation.mutate({ id: u.id, active: !u.active })}
                      disabled={!isRazAdmin || toggleActiveMutation.isPending}
                      className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full transition-all"
                      style={{
                        background: u.active ? "#D1FAE5" : "#F3F4F6",
                        color: u.active ? "#10B981" : "#9CA3AF",
                        cursor: isRazAdmin ? "pointer" : "default",
                      }}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: u.active ? "#10B981" : "#D1D5DB" }} />
                      {u.active ? "Ativo" : "Inativo"}
                    </button>

                    {/* Ações */}
                    {isRazAdmin && (
                      <button
                        onClick={() => { if (confirm(`Remover ${u.name} da plataforma?`)) deleteMutation.mutate({ id: u.id }); }}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-50 transition-colors mx-auto"
                        title="Remover usuário"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Info permissões */}
        {!isRazAdmin && usuarios.length > 0 && (
          <div className="mt-4 flex items-center gap-2 px-4 py-3 rounded-xl border border-[var(--solar-line)] bg-white shadow-[var(--elev-1)] text-xs text-[var(--solar-mute)]">
            <svg className="w-4 h-4 shrink-0 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Apenas administradores RAZ podem alterar perfis e status de usuários.
          </div>
        )}
      </div>
    </div>
  );
}
